/**
 * Unix Time Converter View
 * Interactive Unix timestamp conversion
 */
import React, { useState } from 'react';
import { Box, Text, useInput } from 'ink';
import TextInput from 'ink-text-input';
import { UtilsService } from '../../../services/utils.service.js';

type Mode = 'now' | 'to-human' | 'to-unix';

export const UnixTimeView: React.FC = () => {
    const [input, setInput] = useState('');
    const [mode, setMode] = useState<Mode>('now');
    const [format, setFormat] = useState<'iso' | 'local' | 'utc'>('iso');
    const [useMs, setUseMs] = useState(false);
    const [result, setResult] = useState('');
    const [error, setError] = useState('');
    const [focusedField, setFocusedField] = useState<'mode' | 'input' | 'format' | 'ms' | 'convert'>('mode');
    const [copied, setCopied] = useState(false);

    const service = new UtilsService();

    useInput((input, key) => {
        if (key.tab) {
            const fields: typeof focusedField[] = mode === 'now'
                ? ['mode', 'ms', 'convert']
                : mode === 'to-human'
                ? ['mode', 'input', 'format', 'convert']
                : ['mode', 'input', 'convert'];
            const currentIndex = fields.indexOf(focusedField);
            setFocusedField(fields[(currentIndex + 1) % fields.length]);
        } else if (key.return) {
            if (focusedField === 'mode') {
                const modes: Mode[] = ['now', 'to-human', 'to-unix'];
                const currentIndex = modes.indexOf(mode);
                setMode(modes[(currentIndex + 1) % modes.length]);
                setResult('');
                setError('');
            } else if (focusedField === 'format') {
                const formats: typeof format[] = ['iso', 'local', 'utc'];
                const currentIndex = formats.indexOf(format);
                setFormat(formats[(currentIndex + 1) % formats.length]);
            } else if (focusedField === 'ms') {
                setUseMs(!useMs);
            } else {
                // Convert on Enter from input or convert button
                handleConvert();
            }
        } else if (input === 'c' && result) {
            handleCopy();
        }
    });

    const handleConvert = () => {
        try {
            setError('');
            if (mode === 'now') {
                const timestamp = service.unixTimeCurrent(useMs);
                setResult(timestamp.toString());
            } else if (mode === 'to-human') {
                if (!input) {
                    setError('Please enter a timestamp');
                    return;
                }
                const human = service.unixTimeToHuman(parseInt(input, 10), format);
                setResult(human);
            } else {
                if (!input) {
                    setError('Please enter a date string');
                    return;
                }
                const timestamp = service.unixTimeFromString(input);
                setResult(timestamp.toString());
            }
            setCopied(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Conversion failed');
            setResult('');
        }
    };

    const handleCopy = async () => {
        try {
            const { default: clipboardy } = await import('clipboardy');
            await clipboardy.write(result);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            // Clipboard not available
        }
    };

    return (
        <Box flexDirection="column">
            <Box marginBottom={1}>
                <Text bold color="cyan">
                    🕒 Unix Time Converter
                </Text>
            </Box>

            <Box
                flexDirection="column"
                borderStyle="single"
                borderColor="gray"
                paddingX={2}
                paddingY={1}
                marginBottom={1}
            >
                <Text bold color="yellow" marginBottom={1}>
                    Options:
                </Text>

                <Box marginBottom={1}>
                    <Box width={20}>
                        <Text color={focusedField === 'mode' ? 'green' : undefined}>
                            {focusedField === 'mode' ? '▶ ' : '  '}Mode:
                        </Text>
                    </Box>
                    <Text bold color={focusedField === 'mode' ? 'yellow' : undefined}>
                        {mode === 'now' ? 'CURRENT TIME' : mode === 'to-human' ? 'TIMESTAMP → HUMAN' : 'HUMAN → TIMESTAMP'}
                    </Text>
                    {focusedField === 'mode' && (
                        <Text dimColor> (Enter to cycle)</Text>
                    )}
                </Box>

                {mode !== 'now' && (
                    <Box marginBottom={1} flexDirection="column">
                        <Box marginBottom={0}>
                            <Text color={focusedField === 'input' ? 'green' : undefined}>
                                {focusedField === 'input' ? '▶ ' : '  '}
                                {mode === 'to-human' ? 'Timestamp:' : 'Date string:'}
                            </Text>
                        </Box>
                        <Box marginLeft={2} width={50}>
                            {focusedField === 'input' ? (
                                <TextInput
                                    value={input}
                                    onChange={setInput}
                                    placeholder={mode === 'to-human' ? '1702550400' : '2024-01-15 10:30:00'}
                                />
                            ) : (
                                <Text dimColor={!input}>{input || '(empty)'}</Text>
                            )}
                        </Box>
                    </Box>
                )}

                {mode === 'to-human' && (
                    <Box marginBottom={1}>
                        <Box width={20}>
                            <Text color={focusedField === 'format' ? 'green' : undefined}>
                                {focusedField === 'format' ? '▶ ' : '  '}Format:
                            </Text>
                        </Box>
                        <Text bold color={focusedField === 'format' ? 'yellow' : undefined}>
                            {format.toUpperCase()}
                        </Text>
                        {focusedField === 'format' && (
                            <Text dimColor> (Enter to cycle)</Text>
                        )}
                    </Box>
                )}

                {mode === 'now' && (
                    <Box marginBottom={1}>
                        <Text color={focusedField === 'ms' ? 'green' : undefined}>
                            {focusedField === 'ms' ? '▶ ' : '  '}
                            [{useMs ? '✓' : ' '}] Milliseconds
                        </Text>
                    </Box>
                )}

                <Box marginTop={1}>
                    <Text
                        bold
                        backgroundColor={focusedField === 'convert' ? 'green' : undefined}
                        color={focusedField === 'convert' ? 'black' : 'green'}
                    >
                        {focusedField === 'convert' ? '▶ ' : '  '}
                        [ {mode === 'now' ? 'Get Current' : 'Convert'} ]
                    </Text>
                </Box>
            </Box>

            {error && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="red"
                    paddingX={2}
                    paddingY={1}
                    marginBottom={1}
                >
                    <Text color="red">✗ Error: {error}</Text>
                </Box>
            )}

            {result && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="green"
                    paddingX={2}
                    paddingY={1}
                >
                    <Box marginBottom={1}>
                        <Text bold color="green">
                            ✓ Result:
                        </Text>
                        {copied && (
                            <Box marginLeft={2}>
                                <Text color="green">✓ Copied to clipboard!</Text>
                            </Box>
                        )}
                    </Box>

                    <Box flexDirection="column">
                        <Box marginTop={1}>
                            <Text wrap="wrap">{result}</Text>
                        </Box>
                    </Box>
                </Box>
            )}

            <Box marginTop={1} borderStyle="single" borderColor="gray" paddingX={2}>
                <Text dimColor>
                    Tab: Next | Enter: Toggle/Convert | C: Copy | Esc: Back
                </Text>
            </Box>
        </Box>
    );
};
